import React, { useEffect, useState, useRef } from "react";
import { Outlet, useNavigate, NavLink, useLocation } from "react-router-dom";
import {
  FiHome,
  FiUsers,
  FiFolder,
  FiFileText,
  FiMessageSquare,
  FiDollarSign,
  FiLogOut,
  FiMenu,
  FiX,
} from "react-icons/fi";
import { useVerifyAdminSessionQuery } from "../redux/slices/api/adminApiSlice";

const AdminLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const hasRedirected = useRef(false);

  const adminToken = localStorage.getItem("adminToken");
  
  const { data, error, isLoading } = useVerifyAdminSessionQuery(undefined, {
    skip: !adminToken,
  });

  useEffect(() => {
    // Prevent redirect loop - only redirect once
    if (hasRedirected.current) return;
    
    // If no token, redirect to login
    if (!adminToken) {
      hasRedirected.current = true;
      navigate("/admin/login", { replace: true });
      return;
    }
    
    // If token exists but session verification fails (401/403), clear token and redirect
    if (error) {
      // Only redirect on authentication errors
      if (error.status === 401 || error.status === 403) {
        hasRedirected.current = true;
        localStorage.removeItem("adminToken");
        navigate("/admin/login", { replace: true });
      }
      // For other errors (network, server errors), don't redirect - just show the dashboard
    }
  }, [adminToken, error, navigate]);

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    navigate("/admin/login");
  };

  const navItems = [
    { path: "/admin", icon: FiHome, label: "Dashboard", exact: true },
    { path: "/admin/users", icon: FiUsers, label: "Users" },
    { path: "/admin/projects", icon: FiFolder, label: "Projects" },
    { path: "/admin/results", icon: FiFileText, label: "Results" },
    { path: "/admin/communities", icon: FiMessageSquare, label: "Communities" },
    { path: "/admin/funds", icon: FiDollarSign, label: "Funds" },
  ];

  if (isLoading) {
    return (
      <div className="fixed inset-0 w-screen h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 w-screen h-screen bg-gray-100 flex overflow-hidden">
      {/* Sidebar - Desktop */}
      <aside
        className={`${
          sidebarOpen ? "w-64" : "w-20"
        } hidden lg:flex flex-col bg-secondary text-white transition-all duration-300 ease-in-out`}
      >
        <div className="p-4 flex items-center justify-between border-b border-dark-purple">
          {sidebarOpen && (
            <h1 className="text-xl font-bold bg-gradient-to-r from-primary to-primary-green bg-clip-text text-transparent">
              FunDeSci Admin
            </h1>
          )}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 hover:bg-dark-purple rounded-lg transition-colors"
          >
            <FiMenu size={20} />
          </button>
        </div>

        <nav className="flex-1 py-4">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.exact}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 mx-2 rounded-lg transition-all ${
                  isActive
                    ? "bg-primary text-white"
                    : "text-gray-300 hover:bg-dark-purple hover:text-white"
                }`
              }
            >
              <item.icon size={20} />
              {sidebarOpen && <span>{item.label}</span>}
            </NavLink>
          ))}
        </nav>

        <div className="p-4 border-t border-dark-purple">
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-4 py-3 w-full rounded-lg text-gray-300 hover:bg-dark-purple hover:text-white transition-all"
          >
            <FiLogOut size={20} />
            {sidebarOpen && <span>Logout</span>}
          </button>
        </div>
      </aside>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar - Mobile */}
      <aside
        className={`${
          mobileMenuOpen ? "translate-x-0" : "-translate-x-full"
        } fixed inset-y-0 left-0 w-64 bg-secondary text-white z-50 lg:hidden transition-transform duration-300 ease-in-out`}
      >
        <div className="p-4 flex items-center justify-between border-b border-dark-purple">
          <h1 className="text-xl font-bold bg-gradient-to-r from-primary to-primary-green bg-clip-text text-transparent">
            FunDeSci Admin
          </h1>
          <button
            onClick={() => setMobileMenuOpen(false)}
            className="p-2 hover:bg-dark-purple rounded-lg transition-colors"
          >
            <FiX size={20} />
          </button>
        </div>

        <nav className="flex-1 py-4">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.exact}
              onClick={() => setMobileMenuOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 mx-2 rounded-lg transition-all ${
                  isActive
                    ? "bg-primary text-white"
                    : "text-gray-300 hover:bg-dark-purple hover:text-white"
                }`
              }
            >
              <item.icon size={20} />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="p-4 border-t border-dark-purple">
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-4 py-3 w-full rounded-lg text-gray-300 hover:bg-dark-purple hover:text-white transition-all"
          >
            <FiLogOut size={20} />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        {/* Top Bar */}
        <header className="bg-white shadow-sm py-4 px-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="p-2 hover:bg-gray-100 rounded-lg lg:hidden"
            >
              <FiMenu size={20} />
            </button>
            <h2 className="text-lg font-semibold text-secondary">
              {navItems.find(
                (item) =>
                  item.exact
                    ? location.pathname === item.path
                    : location.pathname.startsWith(item.path) &&
                      item.path !== "/admin"
              )?.label ||
                (location.pathname === "/admin" ? "Dashboard" : "Admin Panel")}
            </h2>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-600">
              Welcome, {data?.admin?.username || "Admin"}
            </span>
            <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center text-white font-semibold">
              {(data?.admin?.username || "A")[0].toUpperCase()}
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-6 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
